import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { auth as authApi } from "../../services/api";
import { Radar } from "lucide-react";
import { useTranslation } from "react-i18next";

function VerifyEmail() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const { t } = useTranslation();

  // If already verified, go to onboarding
  React.useEffect(() => {
    if (user?.emailVerified) {
      navigate("/profile/edit", { replace: true });
    }
  }, [user, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      await authApi.verify(token, code.trim());
      await refreshUser();
      navigate("/profile/edit", { replace: true });
    } catch (err) {
      setError(err.data?.error || err.message || t("verify.verifyFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResending(true);
    setResent(false);
    try {
      await authApi.resendCode(token);
      setResent(true);
    } catch (err) {
      setError(err.data?.error || err.message || t("verify.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  if (!user) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-th-bg px-6">
        <p className="text-th-muted">{t("verify.loginFirst")}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
    <div className="min-h-full flex flex-col items-center justify-center px-6 py-8">
      <div className="w-14 h-14 rounded-2xl bg-th-surface border border-th-border flex items-center justify-center mb-6">
        <Radar size={26} className="text-th-accent" />
      </div>
      <h1 className="text-2xl font-bold text-th-text mb-2">{t("verify.checkEmail")}</h1>
      <p className="text-sm text-th-muted text-center mb-6">
        {t("verify.codeSent")} <span className="text-th-text font-medium">{user.email}</span>
      </p>

      <form onSubmit={handleVerify} className="w-full max-w-sm space-y-4">
        {error && (
          <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">
            {error}
          </p>
        )}
        {resent && (
          <p className="text-sm text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 rounded-xl">
            {t("verify.codeSentAgain")}
          </p>
        )}
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={t("verify.enterCode")}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          maxLength={6}
          className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text text-center tracking-[0.5em] font-mono placeholder-th-muted placeholder:tracking-normal focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
        <button
          type="submit"
          disabled={submitting || code.length !== 6}
          className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-50"
        >
          {submitting ? t("verify.verifying") : t("verify.verify")}
        </button>
      </form>

      <button
        onClick={handleResend}
        disabled={resending}
        className="mt-4 text-sm text-th-accent font-medium disabled:opacity-50"
      >
        {resending ? t("verify.sending") : t("verify.resendCode")}
      </button>

      <p className="text-xs text-th-muted mt-6">{t("verify.codeExpiry")}</p>
    </div>
    </div>
  );
}

export default VerifyEmail;
